<template>
  <div class="form">
    <h4 class="mb-2">{{ $t("CampaignImage.TitleAdd") }}</h4>
    <dx-validation-group ref="validation-group">
      <div class="dx-fieldset">
        <div class="dx-field">
          <div class="dx-field-label">
            {{ this.$t("CampaignImage.Type") }}
          </div>
          <div class="dx-field-value">
            <DxSelectBox
              :items="imageTypes"
              :value.sync="idImageType"
              display-expr="description"
              value-expr="id"
            />
          </div>
        </div>
        <div class="dx-field">
          <div class="dx-field-label">
            {{ this.$t("CampaignImage.Categories") }}
          </div>
          <div class="dx-field-value">
            <DxTagBox
              :value.sync="categories"
              :items="availableCategories"
              :accept-custom-value="true"
            />
          </div>
        </div>
        <div class="dx-field">
          <div class="dx-field-label">
            {{ this.$t("CampaignImage.Image") }}
          </div>
          <div class="dx-field-value">
            <div
              id="dropzone-external"
              class="flex-box"
              :class="[
                isDropZoneActive
                  ? 'dx-theme-accent-as-border-color dropzone-active'
                  : 'dx-theme-border-color',
              ]"
            >
              <img
                id="dropzone-image"
                :src="imageSource"
                v-if="imageSource"
                alt=""
              />
              <div id="dropzone-text" class="flex-box" v-if="textVisible">
                <span>{{ $t("CampaignImage.UploadImageText1") }}</span>
                <span>{{ $t("CampaignImage.UploadImageText2") }}</span>
              </div>
              <DxProgressBar
                id="upload-progress"
                :min="0"
                :max="100"
                width="30%"
                :show-status="false"
                :visible="progressVisible"
                :value="progressValue"
              />
            </div>
            <DxFileUploader
              id="file-uploader"
              dialog-trigger="#dropzone-external"
              drop-zone="#dropzone-external"
              :multiple="false"
              :allowed-file-extensions="allowedFileExtensions"
              upload-mode="instantly"
              :visible="false"
              @drop-zone-enter="onDropZoneEnter"
              @drop-zone-leave="onDropZoneLeave"
              @uploaded="onUploaded"
              @progress="onProgress"
              @upload-started="onUploadStarted"
              @value-changed="onValueChanged"
            />
          </div>
        </div>
        <div class="mt-2">
          <vs-button
            class="ml-4 float-right"
            type="border"
            @click="cancel"
            >{{ $t("Cancel") }}</vs-button
          >
          <vs-button
            @click="addImage"
            class="float-right"
            >{{ this.$t("CampaignImage.SubmitAdd") }}</vs-button
          >
        </div>
      </div>
    </dx-validation-group>
  </div>
</template>


<script>
import moduleCampaignManagement from '@/store/campaign-management/moduleCampaignManagement.js'
import DxSelectBox from 'devextreme-vue/select-box'
import DxButton from 'devextreme-vue/button'
import DxValidationGroup from 'devextreme-vue/validation-group'
import DxValidationSummary from 'devextreme-vue/validation-summary'
import DxTagBox from 'devextreme-vue/tag-box'
import { notifyMixin } from '@/mixins/notifyMixin'
import { DxFileUploader } from 'devextreme-vue/file-uploader'
import { DxProgressBar } from 'devextreme-vue/progress-bar'

export default {
  mixins: [notifyMixin],
  data () {
    return {
      idImageType: 2,
      categories: [],
      availableCategories: [],
      isDropZoneActive: false,
      imageSource: '',
      textVisible: true,
      progressVisible: false,
      progressValue: 0,
      allowedFileExtensions: ['.jpg', '.jpeg', '.gif', '.png', '.heic'],
      imageName: ''
    }
  },
  components: {
    DxButton,
    DxSelectBox,
    DxValidationSummary,
    DxValidationGroup,
    DxTagBox,
    DxFileUploader,
    DxProgressBar
  },
  props: {
    idCampaign: Number
  },
  created () {
    if (!moduleCampaignManagement.isRegistered) {
      this.$store.registerModule(
        'campaignManagement',
        moduleCampaignManagement
      )
      moduleCampaignManagement.isRegistered = true
    }
    this.fetchCategories(this.idCampaign)
  },
  methods: {
    fetchCategories (idCampaign) {
      this.$store.dispatch('campaignManagement/fetchCampaignCategories', idCampaign)
        .then(res => {
          this.availableCategories = res.data.categories
        })
    },
    cancel () {
       this.$emit('closed')
    },
    addImage () {
      const validationGroup = this.$refs["validation-group"].instance
      const result = validationGroup.validate()
      if (!this.imageName) {
        this.notifyError({ message: this.$t("CampaignImage.ImageUploadRequired") })
        return
      }
      if (result.isValid) {
        this.$vs.loading()
        const payload = {
          idCampaign: this.idCampaign,
          idImageType: this.idImageType,
          categories: this.categories,
          imageName: this.imageName
        }
        this.$store
          .dispatch(
            'campaignManagement/addImage',
            payload
          )
          .then(() => {
            this.$vs.loading.close()
            this.notifySuccess(this.$t("CampaignImage.Added"))
            this.$emit('state-changed')
          })
          .catch((error) => {
            this.notifyError(error)
            this.$vs.loading.close()
          })
      }
    },
    onDropZoneEnter (e) {
      if (e.dropZoneElement.id === 'dropzone-external') {
        this.isDropZoneActive = true
      }
    },
    onDropZoneLeave (e) {
      if (e.dropZoneElement.id === 'dropzone-external') {
        this.isDropZoneActive = false
      }
    },
    onUploaded (e) {
      const file = e.file
      const fileReader = new FileReader()
      fileReader.onload = () => {
        this.isDropZoneActive = false
        this.imageSource = fileReader.result
      }
      fileReader.readAsDataURL(file)
      this.textVisible = false
      this.progressVisible = false
      this.progressValue = 0
    },
    onValueChanged (e) {
      this.imageName = e.value[0].name
      const uploadUrl = `https://cdn.markanda.nl/g/upload/UsaCampaign/${this.idCampaign}/${this.imageName}`
      e.component.option("uploadUrl", uploadUrl)
    },
    onProgress (e) {
      this.progressValue = e.bytesLoaded / e.bytesTotal * 100
    },
    onUploadStarted () {
      this.imageSource = ''
      this.progressVisible = true
    }
  },
  computed: {
    imageTypes () {
      return this.$store.state.refCodes.imageTypes.map(a => {
        return { ...a, description: this.$t(`ImageTypeList.${a.description}`) }
      })
    }
  }
}
</script>

<style>
#dropzone-external {
  width: 350px;
  height: 350px;
  background-color: rgba(183, 183, 183, 0.1);
  border-width: 2px;
  border-style: dashed;
  padding: 10px;
}
#dropzone-external > * {
  pointer-events: none;
}
#dropzone-external.dropzone-active {
  border-style: solid;
}
.widget-container > span {
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 16px;
}
#dropzone-image {
  max-width: 100%;
  max-height: 100%;
}
#dropzone-text > span {
  font-weight: 100;
  opacity: 0.5;
}
#upload-progress {
  display: flex;
  margin-top: 10px;
}
.flex-box {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>
